// Card de uma Secretaria (Prefeitura) com o malote do mês entregue, na coluna "Cobrança enviada"
// do quadro de Faturas e Cobranças. Substitui os cards das UCs dela e o do boleto agrupado
// (ver lib/colunaDoQuadro.ts). O clique abre o mês em Agrupamentos de Faturamento, onde ficam a
// relação das UCs, o boleto e o registro do pagamento.

import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, Layers } from 'lucide-react';
import { formatDate } from '../../../src/lib/utils';
import { dataParaCompetencia } from '../../../src/lib/fechamentoAgrupamento';
import type { MaloteNoQuadro } from '../lib/colunaDoQuadro';

interface Props {
  malote: MaloteNoQuadro;
  /** Vencimento do boleto agrupado, quando ele existe. */
  vencimentoBoleto?: string | null;
  /** Selo da situação do boleto agrupado (o mesmo dos outros cards: em aberto, vencido, pago...). */
  seloBoleto?: React.ReactNode;
}

const CardMaloteSecretaria: React.FC<Props> = ({ malote: m, vencimentoBoleto, seloBoleto }) => {
  const mes = dataParaCompetencia(m.competencia) ?? '';
  const destino = `/financeiro/agrupamentos-faturamento?agrupamento=${m.agrupamentoId}${mes ? `&mes=${encodeURIComponent(mes)}` : ''}`;

  let selo: React.ReactNode = seloBoleto ?? null;
  if (m.situacaoPagamento === 'pago_a_menor') {
    selo = (
      <span title="O boleto agrupado foi pago abaixo do esperado" className="flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded">
        <AlertTriangle size={12} /> Pago a menor
      </span>
    );
  } else if (m.faturaBoletoId == null && !(Number(m.valorCobrado) > 0)) {
    selo = (
      <span title="Mês fechado sem valor a cobrar: nenhuma UC teve injeção" className="flex items-center gap-1 text-[10px] font-bold text-gray-600 bg-gray-100 dark:bg-gray-700/60 dark:text-gray-300 px-1.5 py-0.5 rounded">
        Sem cobrança
      </span>
    );
  }

  return (
    <Link
      to={destino}
      title={`Malote de ${m.agrupamentoNome}${mes ? ` (${mes})` : ''} entregue. As UCs desta Secretaria são cobradas neste boleto agrupado. Clique para abrir o mês em Agrupamentos de Faturamento.`}
      className="block p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow bg-indigo-50/60 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-800"
    >
      <div className="flex justify-between items-start gap-2 mb-2">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 rounded">
          <Layers size={11} /> Fatura agrupada
        </span>
        {selo}
      </div>
      <h4 className="text-[12px] font-bold text-gray-900 dark:text-white mb-1 truncate" title={m.agrupamentoNome}>
        Secretaria: {m.agrupamentoNome}
      </h4>
      {m.pagadorNome && (
        <p className="text-[12px] text-primary dark:text-primary-light font-bold truncate mb-1" title={m.pagadorNome}>Pagador: {m.pagadorNome}</p>
      )}
      <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
        {mes}{mes && ' • '}{m.ucs} UC{m.ucs === 1 ? '' : 's'}
        {m.ucsComValor > 0 && m.ucsComValor !== m.ucs && ` (${m.ucsComValor} com valor)`}
        <div className="mt-0.5">Malote entregue em <span className="font-medium">{formatDate(m.maloteEntregueEm)}</span></div>
        {vencimentoBoleto && (
          <div className="mt-0.5">Venc. boleto: <span className="font-medium">{formatDate(vencimentoBoleto)}</span></div>
        )}
      </div>
      <div className="flex justify-between items-end mt-2 pt-2 border-t border-indigo-100 dark:border-indigo-800/60">
        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-700 dark:text-indigo-300">
          Abrir em Agrupamentos <ArrowUpRight size={11} />
        </span>
        <div className="text-right">
          <p className="text-[10px] text-gray-400">Valor do boleto</p>
          <p className="text-sm font-bold text-gray-900 dark:text-white">
            {m.valorCobrado == null
              ? '—'
              : `R$ ${m.valorCobrado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </p>
        </div>
      </div>
    </Link>
  );
};

export default CardMaloteSecretaria;
